import React from 'react';
import {Plus, Trash2, ArrowUp, ArrowDown} from 'lucide-react';
import FormField from '../../../Presentation/components/common/FormField';
import Button from '../../../Presentation/components/ui/Button';
import {ChecklistItem, ChecklistItemType, ITEM_TYPE_LABELS, emptyChecklistItem} from '../models/checklist.model';

interface ChecklistItemsEditorProps {
  items: ChecklistItem[];
  onChange: (items: ChecklistItem[]) => void;
  disabled?: boolean;
  error?: string;
}

const TYPE_OPTIONS: Array<{value: ChecklistItemType; label: string}> = (
  Object.keys(ITEM_TYPE_LABELS) as ChecklistItemType[]
).map((t) => ({value: t, label: ITEM_TYPE_LABELS[t]}));

const ChecklistItemsEditor: React.FC<ChecklistItemsEditorProps> = ({items, onChange, disabled, error}) => {
  const update = (index: number, patch: Partial<ChecklistItem>) => {
    onChange(items.map((it, i) => (i === index ? {...it, ...patch} : it)));
  };

  const addItem = () => onChange([...items, emptyChecklistItem(items.length)]);

  const removeItem = (index: number) =>
    onChange(items.filter((_, i) => i !== index).map((it, i) => ({...it, display_order: i})));

  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next.map((it, i) => ({...it, display_order: i})));
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-2">
        <label className="font-bold text-gray-700">بنود القائمة</label>
        <Button type="button" variant="outline" onClick={addItem} disabled={disabled} icon={<Plus size={16} />}>
          إضافة بند
        </Button>
      </div>

      {error && <p className="text-red-600 text-sm mb-2">{error}</p>}

      {items.length === 0 && (
        <p className="text-grayUnit text-sm py-4 text-center border border-dashed rounded-lg">
          لا توجد بنود بعد — أضف بنداً واحداً على الأقل.
        </p>
      )}

      <div className="space-y-3">
        {items.map((item, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-3 items-end border rounded-lg p-3 bg-gray-50">
            <div className="col-span-12 md:col-span-5">
              <FormField
                type="text"
                label="اسم البند"
                placeholder="مثال: المتجر مفتوح"
                value={item.item_name}
                onChange={(e) => update(idx, {item_name: e.target.value})}
                disabled={disabled}
              />
            </div>
            <div className="col-span-7 md:col-span-3">
              <FormField
                type="select"
                label="النوع"
                value={item.item_type}
                options={TYPE_OPTIONS}
                onChange={(e) => update(idx, {item_type: e.target.value as ChecklistItemType})}
                disabled={disabled}
              />
            </div>
            <div className="col-span-5 md:col-span-2">
              <FormField
                type="checkbox"
                label="إجباري"
                value={item.mandatory}
                onChange={(checked: boolean) => update(idx, {mandatory: checked})}
                disabled={disabled}
              />
            </div>
            <div className="col-span-12 md:col-span-2 flex items-center gap-1 justify-end">
              <button type="button" className="p-2 text-gray-500 hover:text-brand-600 disabled:opacity-30"
                onClick={() => move(idx, -1)} disabled={disabled || idx === 0} aria-label="تحريك لأعلى">
                <ArrowUp size={16} />
              </button>
              <button type="button" className="p-2 text-gray-500 hover:text-brand-600 disabled:opacity-30"
                onClick={() => move(idx, 1)} disabled={disabled || idx === items.length - 1} aria-label="تحريك لأسفل">
                <ArrowDown size={16} />
              </button>
              <button type="button" className="p-2 text-red-500 hover:text-red-700 disabled:opacity-30"
                onClick={() => removeItem(idx)} disabled={disabled} aria-label="حذف البند">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ChecklistItemsEditor;
